import React, { useCallback, useMemo } from 'react';
import { MAPPING_FIELDS, PRODUCT_FIELD_OPTIONS } from './configurationConstants';
import { useConfigurationContext } from './Context/ConfigurationContext';
import { MappingRow } from './MappingRow';
import { MappingValue, FieldConfig, MappingField } from './types';
import { getFieldError } from './validationUtils';

type MappingTableProps = {
  id?: string;
  mappings: { [key: string]: MappingValue };
};

const createFieldConfigMap = (fields: FieldConfig[]): Map<string, FieldConfig> => {
  return new Map(fields.map((field) => [field.key, field]));
};

export const MappingTable = ({ id = 'mapping-table', mappings }: MappingTableProps) => {
  const { isEditMode, updateProductMapping, validationErrors } = useConfigurationContext();

  const fieldConfigMap = useMemo(() => createFieldConfigMap(MAPPING_FIELDS), []);

  const handleSourceChange = useCallback((fieldKey: string, source: string) => {
    const fieldConfig = fieldConfigMap.get(fieldKey);
    const newValue = source === 'Fixed Value' && fieldConfig?.fixedValue
      ? fieldConfig.fixedValue
      : '';

    updateProductMapping(fieldKey as MappingField, source, newValue);
  }, [fieldConfigMap, updateProductMapping]);

  const handleValueChange = useCallback((fieldKey: string, value: string) => {
    const currentMapping = mappings[fieldKey];
    updateProductMapping(fieldKey as MappingField, currentMapping?.source || '', value);
  }, [mappings, updateProductMapping]);

  const getFieldErrors = useCallback((fieldKey: string) => {
    const sourceError = getFieldError(validationErrors, `${fieldKey}.source`);
    const valueError = getFieldError(validationErrors, `${fieldKey}.value`);
    return {
      hasSourceError: !!sourceError,
      hasValueError: !!valueError,
    };
  }, [validationErrors]);

  const headerClasses = "tw-py-3 tw-text-left tw-text-sm tw-font-semibold tw-text-[#6B778C] tw-tracking-wider tw-whitespace-nowrap";

  const columnWidths = {
    field: 'tw-w-[30%]',
    source: 'tw-w-[30%]',
    value: 'tw-w-[40%]',
  };

  const renderHeaderCell = (column: keyof typeof columnWidths, label: string) => (
    <th
      key={column}
      className={`${headerClasses} ${columnWidths[column]}`}
      data-testid={`${id}-header-${column}`}
    >
      <div className="tw-flex tw-items-center tw-pl-4">
        <span>{label}</span>
      </div>
    </th>
  );

  return (
    <div
      className="tw-border tw-border-solid tw-rounded-md tw-border-[#DFE0E6]"
      data-testid={id}
    >
      <div style={{ width: '100%', height: 'auto' }}>
        <table
          className="tw-bg-zinc-200 tw-w-full tw-table-fixed"
          data-testid={`${id}-table`}
        >
          <thead
            className="tw-sticky tw-top-0 tw-bg-zinc-200"
            data-testid={`${id}-header`}
          >
            <tr data-testid={`${id}-header-row`}>
              {renderHeaderCell('field', 'SmartStudio Field')}
              {renderHeaderCell('source', 'Source')}
              {renderHeaderCell('value', 'Value')}
            </tr>
          </thead>
          <tbody data-testid={`${id}-body`}>
            {MAPPING_FIELDS.map((fieldConfig, index) => {
              const fieldErrors = getFieldErrors(fieldConfig.key);
              return (
                <MappingRow
                  key={fieldConfig.key}
                  id={`${id}-row-${fieldConfig.key}`}
                  fieldConfig={fieldConfig}
                  mapping={mappings[fieldConfig.key]}
                  isEditMode={isEditMode}
                  isEvenRow={index % 2 === 0}
                  fieldOptions={PRODUCT_FIELD_OPTIONS}
                  onSourceChange={handleSourceChange}
                  onValueChange={handleValueChange}
                  hasSourceError={fieldErrors.hasSourceError}
                  hasValueError={fieldErrors.hasValueError}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};